import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '~/app/auth/context/AuthContext';
import {
  SiteService,
  type SiteAssetMapAsset,
  type SiteLookup,
} from '~/app/services/site/site.service';

export type SiteAssetStatus = 'healthy' | 'warning' | 'critical';

export function getSiteAssetStatus(
  openIncidentsCount: number,
): SiteAssetStatus {
  if (openIncidentsCount === 0) {
    return 'healthy';
  }

  if (openIncidentsCount < 3) {
    return 'warning';
  }

  return 'critical';
}

export function useSiteAssetMap(siteId: string) {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';

  const query = useQuery({
    queryKey: ['sites', 'assets-map', organizationId, siteId],
    queryFn: () => SiteService.findAssetsMapById(organizationId, siteId),
    enabled: Boolean(organizationId) && Boolean(siteId.trim()),
  });

  const site = query.data?.site ?? null;
  const assets = useMemo(() => query.data?.assets ?? [], [query.data?.assets]);

  const statusCounts = useMemo(() => {
    return assets.reduce(
      (accumulator, asset) => {
        const status = getSiteAssetStatus(asset.openIncidentsCount);
        accumulator[status] += 1;
        return accumulator;
      },
      {
        healthy: 0,
        warning: 0,
        critical: 0,
      } satisfies Record<SiteAssetStatus, number>,
    );
  }, [assets]);

  return {
    assets,
    site,
    statusCounts,
    error: query.error ?? null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    refetch: query.refetch,
  } satisfies {
    assets: SiteAssetMapAsset[];
    site: SiteLookup | null;
    statusCounts: Record<SiteAssetStatus, number>;
    error: Error | null;
    isFetching: boolean;
    isLoading: boolean;
    refetch: () => Promise<unknown>;
  };
}
